import { getConfigValue } from '@pressreader/config';

type BrowserType = 'chrome' | 'webkit' | 'opera' | 'trident' | 'msie' | 'mozilla';

const userAgent = navigator.userAgent || '';
const uaMatch = (ua: string) => {
        ua = ua.toLowerCase();

        const match =
            /(chrome)[ /]([\w.]+)/.exec(ua) ||
            /(webkit)[ /]([\w.]+)/.exec(ua) ||
            /(opera)(?:.*version)?[ /]([\w.]+)/.exec(ua) ||
            /(trident)(?:.*rv:([\d.]+))?/.exec(ua) ||
            /(msie) ([\w.]+)/.exec(ua) ||
            (ua.indexOf('compatible') < 0 ? /(mozilla)(?:.*? rv:([\w.]+))?/.exec(ua) : null);

        return {
            browser: (match?.[1] || '') as BrowserType,
            version: match?.[2] || '0',
        };
    },
    matched = uaMatch(userAgent);

const browser = {} as {
    chrome?: boolean;
    webkit?: boolean;
    opera?: boolean;
    trident?: boolean;
    msie?: boolean;
    mozilla?: boolean;

    safari?: boolean;
    chromeIOS?: boolean;
    chromeIOSVer?: string;
    msie_mode?: number;

    playBook?: boolean;
    android?: boolean;
    iOS?: boolean;

    version: string;
    /**
     * True if the browser is running inside any web view (including PressReader mobile app web view).
     */
    isWebView: boolean;
    /**
     * True if the browser is running inside the PressReader app (iOS or Android) web view.
     */
    isWebViewPRApp: boolean;
};

if (matched.browser) {
    browser[matched.browser] = true;
    browser.version = matched.version;
}

if (browser.webkit) {
    // Deprecated, use jQuery.browser.webkit instead
    // Maintained for back-compat only
    browser.safari = true;
    browser.chrome = !!userAgent.match(/Chrome/i);

    // Check if it's Chrome on iOS
    const criosMatch = /(crios)[/]([\d.]+)/.exec(userAgent.toLowerCase());
    if (criosMatch?.[1]?.length) {
        browser.chromeIOS = true;
        browser.chromeIOSVer = criosMatch[2] || '0';
    }
}

// in jquery 1.8 for chrome browsers the "$.browser" will indicate that browser is "chrome" and it is not "webkit"
// so make it to be "webkit"
// NOTE: moved from nd.core
if (browser.chrome) {
    browser.webkit = true;
}

if (browser.trident) {
    browser.msie = true;
}

if (browser.msie) {
    browser.msie_mode = (document as any).documentMode || 0;
}

/**
 * RegExp checks to determine if the browser is running inside a webview.
 * @link https://github.com/atomantic/is-ua-webview/blob/main/data/rules.js
 */
const webViewChecks: RegExp[] = [
    /\(.*\Wwv\W/, // Android 5 and above check: user-agent should contain "wv"
    /Android 4\..*Chrome\/30\.0\.0\.0/, // Android 4 has fixed version of Chrome for web view.
    // if it says it's a webview, let's go with that
    /WebView/,
    // iOS webview will be the same as safari but missing "Safari"
    /(iPhone|iPod|iPad)(?!.*Safari)/,
    // Android Lollipop and Above: webview will be the same as native but it will contain "wv"
    // Android KitKat to Lollipop webview will put Version/X.X Chrome/{version}.0.0.0
    /Android.*(?:;\s+wv|Version\/\d\.\d\s+Chrome\/\d+(?:\.0){3})/,
    // old chrome android webview agent
    /Linux; U; Android/,
];

function isWebViewCheck(ua: string) {
    const isStandaloneSafari = Boolean((navigator as unknown as { standalone: boolean }).standalone); // https://developer.mozilla.org/en-US/docs/Web/API/Navigator "Returns a boolean indicating whether the browser is running in standalone mode. Available on Apple's iOS Safari only."
    const isStandalone = isStandaloneSafari || (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);

    for (const check of webViewChecks) {
        if (check.test(ua) && !isStandalone) {
            return true;
        }
    }
    return false;
}

const defaultInAppUserAgent = 'AppEmbeddedWebView';
Object.defineProperty(browser, 'isWebViewPRApp', {
    get: () => userAgent.includes(getConfigValue<string>('WebView.InAppUserAgent') ?? defaultInAppUserAgent),
    enumerable: true,
    configurable: false,
});
Object.defineProperty(browser, 'isWebView', {
    get: () => browser.isWebViewPRApp || isWebViewCheck(userAgent),
    enumerable: true,
    configurable: false,
});

export { browser };
